import { createSlice, PayloadAction, createListenerMiddleware } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  STORAGE_KEY_ACCOUNT_EXTERNAL_ID,
  STORAGE_KEY_LAST_ACTIVITY,
  TOKEN_EXP_CHECK_INTERVAL_MS,
} from "../envVariables";
import { RootState } from ".";
import { doPeriodicTokenExpirationCheck } from "../services/cpln";
import qs from "qs";

type AlertContent = {
  id: string;
  title: string;
  message: string;
  link: string;
} | null;

export type DashboardOption = "console" | "home" | "task";

interface UIDataState {
  tabId: string;

  // TODO we should parse and generate a state from the url and vice versa
  initialUrl: string;
  accountExternalId: string;
  dashboard: DashboardOption;
  isCreateForm: boolean;

  alertContent: AlertContent | null;
  isAlertClosed: boolean;
  // drawerType?

  scrollTop: number;

  useSplitViewComparison: boolean;
  intervalMs: number;

  lastActivityTimestamp: number;
}

function getLastActivityTimestamp() {
  const valueString = localStorage.getItem(STORAGE_KEY_LAST_ACTIVITY) || "0";
  let value = Number(valueString);
  if (Number.isNaN(value)) {
    value = 0;
  }
  return value;
}

const initialState: UIDataState = {
  tabId: uuidv4(),
  initialUrl: "",
  accountExternalId: "",
  dashboard: "home",
  isCreateForm: false,
  alertContent: null,
  isAlertClosed: false,
  scrollTop: 0,
  useSplitViewComparison: false,
  intervalMs: TOKEN_EXP_CHECK_INTERVAL_MS,
  lastActivityTimestamp: getLastActivityTimestamp(),
};

export const uiDataSlice = createSlice({
  name: "uiData",
  initialState,
  reducers: {
    initializeUiData(state) {
      let initialUrl = window.location.href.split(window.location.host)[1];

      // Strip /-create and /-draft from the URL on first load
      if (initialUrl.includes("/-create")) {
        initialUrl = initialUrl.split("/-create")[0];
      } else if (initialUrl.includes("/-draft")) {
        initialUrl = initialUrl.split("/-draft")[0];
      }

      state.initialUrl = initialUrl;
      const { id } = qs.parse(location.search, {
        ignoreQueryPrefix: true,
      });
      if (id && typeof id === "string") {
        state.accountExternalId = id;
        localStorage.setItem(STORAGE_KEY_ACCOUNT_EXTERNAL_ID, id);
      }
    },
    setInitialUrl: (state, action: PayloadAction<string>) => {
      state.initialUrl = action.payload;
    },
    setAccountExternalId: (state, action: PayloadAction<string>) => {
      state.accountExternalId = action.payload;
      if (action.payload) {
        localStorage.setItem(STORAGE_KEY_ACCOUNT_EXTERNAL_ID, action.payload);
      } else {
        localStorage.removeItem(STORAGE_KEY_ACCOUNT_EXTERNAL_ID);
      }
    },
    setAlertContent(state, action: PayloadAction<AlertContent>) {
      const content = action.payload;
      state.alertContent = content;
      if (content) {
        const localDontShowAgain = localStorage.getItem(content.id) === "true" || false;
        state.isAlertClosed = localDontShowAgain;
      } else {
        state.isAlertClosed = false;
      }
    },
    closeAlert(state) {
      state.isAlertClosed = true;
    },
    setScrollPosition(state, action: PayloadAction<number>) {
      state.scrollTop = action.payload;
    },
    setUseSplitViewComparison(state, action: PayloadAction<boolean>) {
      state.useSplitViewComparison = action.payload;
    },
    parseForDashboard: (state, action: PayloadAction<string>) => {
      const d = parseDashboard(action.payload);
      state.dashboard = d;
      state.isCreateForm = window.location.pathname.includes(`/-create`) || window.location.pathname.includes(`/-edit`);
    },
    updateLastActivityTimestamp(state) {
      state.lastActivityTimestamp = new Date().getTime();
    },
  },
});

export const {
  initializeUiData,
  setInitialUrl,
  setAccountExternalId,
  setAlertContent,
  closeAlert,
  setScrollPosition,
  setUseSplitViewComparison,
  parseForDashboard,
  updateLastActivityTimestamp,
} = uiDataSlice.actions;

export const uiDataListenerMiddleware = createListenerMiddleware();

export const selectInitialUrl = (state: RootState) => state.uiData.initialUrl;

uiDataListenerMiddleware.startListening({
  actionCreator: updateLastActivityTimestamp,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    localStorage.setItem(STORAGE_KEY_LAST_ACTIVITY, String(state.uiData.lastActivityTimestamp));
  },
});

export const uiDataReducer = uiDataSlice.reducer;

export function parseDashboard(location: string): DashboardOption {
  // Parse dashboard
  let dashboard: DashboardOption = "home";
  if (location.startsWith("/console")) {
    dashboard = "console";
  }
  if (location.startsWith("/home")) {
    dashboard = "home";
  }
  if (location.startsWith("/task")) {
    dashboard = "task";
  }
  return dashboard;
}
